import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  AllShiftMaster,
  AddShiftName,
  UpdateShiftDetails,
  getShiftById,
} from "../../Apis/shiftMasterApi";
import TableComponent from "../../Common/TableComponent/tableComponent";
import { TableLoader } from "../../Common/ShimmerLoader";
import { useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import "./shiftMaster.css";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";

const ShiftMaster = ({ t }) => {
  const navigate = useNavigate();
  const headers = [
    "shift",
    "startTime",
    "endTime",
    "shiftCode",
    "bufferMinutesBeforeShift",
    "bufferMinutesAfterShift",
    "status",
    "actions",
  ];
  const [lgShow, setLgShow] = useState(false);
  const [shiftData, setShiftData] = useState([]);
  const [editShiftData, setEditShiftData] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [shiftName, setShiftName] = useState([]);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [sortedData, setSortedData] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [sortConfig, setSortConfig] = useState(null);
  const [formInput, setFormInput] = useState({
    shiftName: "",
    startTime: "",
    endTime: "",
    shiftCode: "",
    bufferMinutesBeforeShiftStart: null, // New field for buffer minutes before shift
    bufferMinutesAfterShiftStart: null,  // New field for buffer minutes after shift
    isActive: true,
  });

  const handleClose = () => {
    setFormInput({
      shiftName: "",
      startTime: "",
      endTime: "",
      shiftCode: "",
      bufferMinutesBeforeShiftStart: "",
      bufferMinutesAfterShiftStart: "",
      isActive: true,
    });
    setLgShow(false);
    setFormSubmitted(false);
  };

  const handleShow = () => {
    setLgShow(true);
    setFormInput({
      shiftName: "",
      startTime: "",
      endTime: "",
      shiftCode: "",
      bufferMinutesBeforeShiftStart: null,
      bufferMinutesAfterShiftStart: null,
      isActive: true,
    });
    setEditShiftData(null);
  };

  const formatTimeTo24Hour = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    return `${hours}:${minutes || "00"}`;
  };

  const handleEditShift = (selectedShift) => {
    setEditShiftData(selectedShift);
    setFormInput({
      shiftName: selectedShift.shiftName,
      startTime: selectedShift.startTime,
      endTime: selectedShift.endTime,
      shiftCode: selectedShift.shiftCode,
      bufferMinutesBeforeShiftStart: selectedShift.bufferMinutesBeforeShiftStart || null,
      bufferMinutesAfterShiftStart: selectedShift.bufferMinutesAfterShiftStart || null,
      isActive: selectedShift.isActive,
      shiftId: selectedShift.shiftId,
    });
    setLgShow(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "startTime" || name === "endTime") {
      const formattedTime = value
        ? value.includes(":")
          ? `${value}:00`
          : `${value}:00:00`
        : "";
      setFormInput((prevData) => ({ ...prevData, [name]: formattedTime }));
    } else if (name === "shiftName") {
      const sanitizedValue = value
      setFormInput((prevData) => ({ ...prevData, [name]: sanitizedValue }));
    } else if (name === "shiftCode") {
      const sanitizedValue = value.toUpperCase().replace(/[^A-Z0-9-]/g, "");
      setFormInput((prevData) => ({ ...prevData, [name]: sanitizedValue }));
    } else if (name === "bufferMinutesBeforeShiftStart" || name === "bufferMinutesAfterShiftStart") {
      // Restrict input to numbers between 0 and 60
      if (/^\d*$/.test(value) && (value === "" || parseInt(value, 10) <= 60)) {
        setFormInput((prevData) => ({ ...prevData, [name]: value }));
      }
    } else {
      setFormInput((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const handleCreateShift = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    try {
      setIsLoading(true);

      if (
        !formInput.shiftName ||
        !formInput.startTime ||
        !formInput.endTime
      ) {
        return;
      }

      if (editShiftData) {
        const updatedShiftData = {
          ...formInput,
          shiftId: editShiftData.shiftId,
        };
        const response = await UpdateShiftDetails(updatedShiftData);
        const updatedShifts = shiftData.map((shift) =>
          shift.shiftId === response.id ? response : shift
        );
        setShiftData(updatedShifts);
        toast.success("Shift updated successfully");
        fetchShiftDetails();
        handleClose();
      } else {
        const response = await AddShiftName({
          ...formInput,
          isActive: formInput.isActive,
        });
        setShiftData([...shiftData, response]);
        toast.success("Shift added successfully");
      }
      fetchShiftDetails();
      handleClose();
    } catch (error) {
      if (error.response) {
        const { status, data } = error.response;
        if (status === 404 || status === 409 || status === 400) {
          toast.error(data, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await AllShiftMaster();
      setShiftData(response);
      setOriginalData(response);
    } catch (error) {
      console.error("Error fetching shift data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleViewShiftData = async (id) => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setIsLoading(true);
      const response = await getShiftById(id);
      const shiftDetails = response.data;
      navigate("/view-shift-details", { state: { shiftDetails } });
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchShiftDetails = async () => {
    const [shiftDetails] = await Promise.all([AllShiftMaster()]);
    const shiftData = shiftDetails.map((station) => ({
      ...station,
      shift: station.shiftName,
      bufferMinutesBeforeShift: station.bufferMinutesBeforeShiftStart || "N/A",
      bufferMinutesAfterShift: station.bufferMinutesAfterShiftStart || "N/A",
      status: station.isActive ? "Active" : "InActive",
    }));

    const sortedshiftData = shiftData.sort((a, b) => {
      if (a.isActive === b.isActive) return 0;
      return a.isActive ? -1 : 1;
    });

    setShiftName(sortedshiftData);
  };

  useEffect(() => {
    fetchShiftDetails();
  }, []);

  const convertTo12HrFormat = (time) => {
    const [hours, minutes] = time.split(":").map(Number);
    const suffix = hours >= 12 ? "PM" : "AM";
    const hour12 = hours % 12 || 12;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    return `${hour12}:${formattedMinutes} ${suffix}`;
  };

  const downloadAsExcel = async (data) => {
    const dataToUse = sortedData.length > 0 ? sortedData : originalData;
    const currentDate = new Date();
    const formattedDate = `${currentDate.getFullYear()}-${currentDate.getMonth() + 1}-${currentDate.getDate()}`;
    const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
    const options = { day: "numeric", month: "numeric", year: "numeric" };
    const generationTime = ` ${new Date().toLocaleString("en-GB", options).replace(/\//g, "/")}`;
    const downloadDateTime = `Shift Report Generated on: ${generationTime},${formattedTime}`;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Shifts");

    const boldFont = { bold: true };

    worksheet.addRow(["", "", downloadDateTime, ""]);
    worksheet.mergeCells(1, 2, 1, 6);
    worksheet.getCell(1, 2).value = downloadDateTime;
    worksheet.getCell(1, 2).font = boldFont;
    worksheet.getCell(1, 2).alignment = { horizontal: "center" };

    for (let col = 2; col <= 6; col++) {
      worksheet.getCell(1, col).font = boldFont;
      worksheet.getCell(1, col).alignment = { horizontal: "center" };
    }

    worksheet.addRow(["", "", "Shift Details", ""]);
    worksheet.mergeCells(2, 2, 2, 6);
    worksheet.getCell(2, 2).value = "Shift Details";
    worksheet.getCell(2, 2).font = boldFont;
    worksheet.getCell(2, 2).alignment = { horizontal: "center" };

    for (let col = 2; col <= 6; col++) {
      worksheet.getCell(2, col).font = boldFont;
      worksheet.getCell(2, col).alignment = { horizontal: "center" };
    }

    worksheet.addRow([""]);

    const tableHeaderRow = worksheet.addRow([
      "S.No",
      "Shift",
      "Start Time",
      "End Time",
      "Shift Code",
      "Buffer Minutes Before Shift",
      "Buffer Minutes After Shift",
      "Status",
    ]);
    tableHeaderRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.alignment = { horizontal: "left" };
      if (colNumber >= 1 && colNumber <= 8) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "003366" },
          bgColor: { argb: "FFFFFFFF" },
        };
      }
    });

    shiftName.forEach((item, index) => {
      worksheet
        .addRow([
          index + 1,
          item.shiftName,
          convertTo12HrFormat(item.startTime),
          convertTo12HrFormat(item.endTime),
          item.shiftCode,
          item.bufferMinutesBeforeShiftStart || "N/A",
          item.bufferMinutesAfterShiftStart|| "N/A",
          item.isActive ? "Active" : "InActive",
        ])
        .eachCell({ includeEmpty: true }, (cell, colNumber) => {
          if (colNumber >= 1 && colNumber <= 8) {
            cell.alignment = { horizontal: "left" };
          } else {
            cell.alignment = { horizontal: "center" };
          }
        });
    });

    worksheet.columns = [
      { width: 5 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 30 },
      { width: 30 },
      { width: 15 },
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ShiftDetails.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  let storedPermissions = localStorage.getItem("permissionId");
  const permissionValues1 = JSON.parse(localStorage.getItem("rolePermissions")) || {};
  let canWriteValue = null;

  storedPermissions = parseInt(storedPermissions, 10);

  const matchingPermission = permissionValues1.find(
    (mapping) => mapping.permissionId === storedPermissions
  );

  if (matchingPermission) {
    canWriteValue = matchingPermission.canWrite;
  }

  const handleSort = (sortConfig) => {
    const sorted = [...shiftName].sort((a, b) => {
      if (sortConfig.order === "asc") {
        return a[sortConfig.column] > b[sortConfig.column] ? 1 : -1;
      } else {
        return a[sortConfig.column] < b[sortConfig.column] ? 1 : -1;
      }
    });
    setSortedData(sorted);
  };

  const handleSortAndExport = async () => {
    if (sortConfig) {
      handleSort(sortConfig);
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    const dataToExport = (sortConfig ? sortedData : originalData)
      .slice()
      .sort((a, b) => {
        if (a.isActive && !b.isActive) return -1;
        if (!a.isActive && b.isActive) return 1;
        return 0;
      });
    downloadAsExcel(dataToExport);
  };

  return (
    <>
      <div style={{ width: "100%" }}>
        <div className="content">
          <div className="page-content">
            <div className="top-header page-title">
              <span>{t("shift")}</span>
            </div>
            <div className="page-header">
              <div className="flex_end_middle mb-3">
                {/* {canWriteValue === true ? ( */}
                  <button className="button-container" onClick={handleShow}>
                    <i className="fa-solid fa-plus me-1 icon-container"></i>
                    {t("addShift")}
                  </button>
                {/* ) : (
                  ""
                )} */}
                <button
                  className="btn btn-excel"
                  onClick={handleSortAndExport}
                >
                  <i className="fa-solid fa-file-excel me-1 icon-container"></i> Excel
                </button>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <TableComponent isLoading={isLoading}
                  DataFetchURL={shiftName}
                  headers={headers}
                  filterFunction={(data) =>
                    data.filter((item) => item.status === 1 || item.status === 2)
                  }
                  editData={handleEditShift}
                  editShiftData={editShiftData}
                  viewData={(id) => handleViewShiftData(id)}
                  onSort={handleSort}
                  statusBadge
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        size="lg"
        show={lgShow}
        onHide={() => handleClose()}
        aria-labelledby="example-modal-sizes-title-lg"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title id="example-modal-sizes-title-lg">
            {editShiftData ? t("editShift") : t("addShift")}
          </Modal.Title>
          <i
            className="fa-solid fa-xmark close-container"
            onClick={handleClose}
          ></i>
        </Modal.Header>
        <hr />
        <Modal.Body>
          <form onSubmit={handleCreateShift}>
            <div className="row">
              <div className="col-md-4 mb-3">
                <div className="form-group">
                  <label className="form-label">
                    Shift <span style={{ color: "#EC1117" }}> *</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter Shift"
                    name="shiftName"
                    className={`form-control ${formInput.shiftName || !formSubmitted ? "" : "input-error"
                      }`}
                    maxLength={50}
                    onChange={handleInputChange}
                    value={formInput.shiftName}
                    // disabled={!canWriteValue}
                  />
                  {!formInput.shiftName && formSubmitted && (
                    <div className="required-message">Shift is Required</div>
                  )}
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <div className="form-group">
                  <label className="form-label">
                    Start Time <span style={{ color: "#EC1117" }}> *</span>
                  </label>
                  <input
                    type="time"
                    name="startTime"
                    className={`form-control ${formInput.startTime || !formSubmitted ? "" : "input-error"
                      }`}
                    onChange={handleInputChange}
                    value={formatTimeTo24Hour(formInput.startTime || "")}
                    // disabled={!canWriteValue}
                  />
                  {!formInput.startTime && formSubmitted && (
                    <div className="required-message">
                      Start Time is Required
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <div className="form-group">
                  <label className="form-label">
                    End Time <span style={{ color: "#EC1117" }}> *</span>
                  </label>
                  <input
                    type="time"
                    name="endTime"
                    className={`form-control ${formInput.endTime || !formSubmitted ? "" : "input-error"
                      }`}
                    onChange={handleInputChange}
                    value={formatTimeTo24Hour(formInput.endTime || "")}
                    // disabled={!canWriteValue}
                  />
                  {!formInput.endTime && formSubmitted && (
                    <div className="required-message">End Time is Required.</div>
                  )}
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <div className="form-group">
                  <label className="form-label">
                    Buffer Mins Before Shift
                  </label>
                  <input
                    type="number"
                    placeholder="Enter Buffer Minutes Before Shift"
                    name="bufferMinutesBeforeShiftStart"
                    className="form-control"
                    onChange={handleInputChange}
                    value={formInput.bufferMinutesBeforeShiftStart}
                    // disabled={!canWriteValue}
                    min="0"
                    max="60"
                    step="1"
                    onKeyDown={(e) => {
                      if (
                        e.key === "e" ||
                        e.key === "+" ||
                        e.key === "-" ||
                        e.key === "."
                      ) {
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <div className="form-group">
                  <label className="form-label">
                    Buffer Mins After Shift
                  </label>
                  <input
                    type="number"
                    placeholder="Enter Buffer Minutes After Shift"
                    name="bufferMinutesAfterShiftStart"
                    className="form-control"
                    onChange={handleInputChange}
                    value={formInput.bufferMinutesAfterShiftStart}
                    // disabled={!canWriteValue}
                    min="0"
                    max="60"
                    step="1"
                    onKeyDown={(e) => {
                      if (
                        e.key === "e" ||
                        e.key === "+" ||
                        e.key === "-" ||
                        e.key === "."
                      ) {
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="col-md-4" style={{ marginTop: "5px" }}>
              <label className="form-label">
                {t("status")} <span style={{ color: "#EC1117" }}> *</span>
              </label>
              <div className="form-group" style={{ display: "flex" }}>
                <div className="form-check">
                  <input
                    type="radio"
                    className="form-check-input"
                    name="isActive"
                    id="activeRadio"
                    checked={formInput.isActive}
                    onChange={() =>
                      handleInputChange({
                        target: { name: "isActive", value: true },
                      })
                    }
                  />
                  <label className="form-check-label" htmlFor="activeRadio">
                    Active
                  </label>
                </div>
                <div className="form-check" style={{ marginLeft: "40px" }}>
                  <input
                    type="radio"
                    className="form-check-input"
                    name="isActive"
                    id="inactiveRadio"
                    checked={formInput.isActive === false}
                    onChange={() =>
                      handleInputChange({
                        target: { name: "isActive", value: false },
                      })
                    }
                  />
                  <label className="form-check-label" htmlFor="inactiveRadio">
                    InActive
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-center">
              {/* {canWriteValue === true ? ( */}
                <button type="submit" className="deployment-button">
                  <i className="fa-solid fa-check done-container"></i>
                  {editShiftData ? t("Update") : t("Save")}
                </button>
              {/* ) : (
                ""
              )} */}
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default withTranslation()(ShiftMaster);